import {
  createTheme,
  defaultVariantColorsResolver,
  parseThemeColor,
  rem,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type VariantColorsResolver,
} from '@mantine/core';

/**
 * Carpool visual language (see frontend/ccms_rides/DESIGN.md).
 *
 * Deep teal primary, warm neutrals, Fraunces for display titles and Public Sans for everything
 * else. The colour tokens live in styles/global.css (`--rides-*`) so plain CSS and Mantine share
 * them in both colour schemes. Every text / background pair here meets WCAG 2.1 AA (4.5:1).
 *
 * Shade rules: light mode uses shade 7 of every colour (filled backgrounds, text, borders) and
 * dark mode uses shade 4 for filled backgrounds and shade 0 for "light" variant text. The custom
 * tuples below are tuned so shade 7 carries white text and shade 4 carries dark ink at ≥ 4.5:1.
 */
const brand: MantineColorsTuple = [
  '#e4f3f2',
  '#c6e5e3',
  '#9dd3cf',
  '#72c0ba',
  '#54b7b0', // dark-mode primary
  '#45a29b',
  '#2d7f7a',
  '#1f6f6b', // light-mode primary
  '#175551',
  '#0f3d3a',
];

/** Highlights only (the ride-today dot). Text in orange uses shade 7. */
const accent: MantineColorsTuple = [
  '#fdf1e8',
  '#fadfcb',
  '#f5c3a0',
  '#f0a573',
  '#ee9a5f',
  '#e88747',
  '#dd7a3c',
  '#a85326',
  '#8a4420',
  '#6c351a',
];

/** Errors and destructive actions. Shade 7 = 6.1:1 with white; shade 4 = 6.3:1 with dark ink. */
const red: MantineColorsTuple = [
  '#fdeceb',
  '#fad4d0',
  '#f4aaa2',
  '#ee8579',
  '#ef7d70',
  '#f2938a',
  '#cf4130',
  '#b5321f',
  '#99291a',
  '#7d2115',
];

/** Success. Mirrors --rides-ok. */
const green: MantineColorsTuple = [
  '#e6f4ec',
  '#c5e6d2',
  '#9bd4b2',
  '#6fc290',
  '#5cc78a',
  '#72d29b',
  '#2f8f5b',
  '#24724a',
  '#1c5c3b',
  '#14452c',
];

/** Warnings and gentle reminders. Mirrors --rides-warn. */
const yellow: MantineColorsTuple = [
  '#fdf5e1',
  '#faeac0',
  '#f5d88c',
  '#efc65a',
  '#e2b055',
  '#e9be6d',
  '#a97a1c',
  '#8a5a12',
  '#6f480e',
  '#55370b',
];

/**
 * Dark-mode neutrals: Mantine's default `dark` palette is a warm grey that clashes with the
 * blue-grey cards, so it is replaced with the Carpool surfaces.
 *   0 text · 2 dimmed · 3 placeholder · 4 borders · 5 hover · 6 inputs · 7 surface (cards, menus) · 8 page
 */
const dark: MantineColorsTuple = [
  '#e9eef2',
  '#c9d2da',
  '#9aa6b2',
  '#8d9aa6',
  '#6b7884',
  '#2f3a44',
  '#232c34',
  '#1a2128',
  '#10151a',
  '#0b0f13',
];

/**
 * Filled buttons, badges and icons: white text in light mode, dark ink in dark mode (the dark
 * filled shades are light, so white text would fail at about 2.4:1). `--rides-on-filled` flips
 * with the colour scheme in global.css. Explicit shades (`color="red.9"`) keep Mantine's rule.
 */
const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  if (input.variant !== 'filled' || !input.color) return resolved;
  const parsed = parseThemeColor({ color: input.color, theme: input.theme });
  if (parsed.isThemeColor && parsed.shade === undefined) {
    return { ...resolved, color: 'var(--rides-on-filled)' };
  }
  return resolved;
};

/** Help text ("Optional", hints) and errors sit BELOW the field, so inputs side by side stay aligned. */
const HELP_BELOW: ('label' | 'input' | 'description' | 'error')[] = ['label', 'input', 'description', 'error'];

export const theme = createTheme({
  primaryColor: 'brand',
  primaryShade: { light: 7, dark: 4 },
  colors: { brand, accent, red, green, yellow, orange: accent, teal: brand, dark },
  black: '#1b2730',
  white: '#ffffff',
  defaultRadius: 'lg',
  radius: { xs: rem(6), sm: rem(10), md: rem(12), lg: rem(14), xl: rem(20) },
  fontFamily: '"Public Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  headings: {
    fontFamily: '"Fraunces", Georgia, "Times New Roman", serif',
    fontWeight: '600',
    sizes: {
      h1: { fontSize: rem(28), lineHeight: '1.2' },
      h2: { fontSize: rem(22), lineHeight: '1.25' },
      h3: { fontSize: rem(18), lineHeight: '1.3' },
      h4: { fontSize: rem(16), lineHeight: '1.35' },
    },
  },
  // 16 px body text; 14 px is the smallest size members read (legal small print may use 13).
  fontSizes: { xs: rem(14), sm: rem(15), md: rem(16), lg: rem(18), xl: rem(20) },
  lineHeights: { xs: '1.45', sm: '1.5', md: '1.55', lg: '1.5', xl: '1.4' },
  focusRing: 'auto',
  cursorType: 'pointer',
  variantColorResolver,
  components: {
    InputWrapper: { defaultProps: { inputWrapperOrder: HELP_BELOW } },
    Button: {
      defaultProps: { size: 'md', radius: 'lg' },
      styles: { root: { minHeight: rem(48), fontWeight: 600 } },
    },
    TextInput: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    PasswordInput: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    Select: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    NativeSelect: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    MultiSelect: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    Autocomplete: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    NumberInput: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    Textarea: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    FileInput: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    DateInput: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    DatePickerInput: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    TimeInput: { defaultProps: { size: 'md', radius: 'md', inputWrapperOrder: HELP_BELOW } },
    Checkbox: { defaultProps: { size: 'md' } },
    Radio: { defaultProps: { size: 'md' } },
    Switch: { defaultProps: { size: 'md' } },
    SegmentedControl: { defaultProps: { size: 'md', radius: 'lg' } },
    CloseButton: { defaultProps: { size: 'xl', iconSize: 20 } },
    Anchor: { defaultProps: { underline: 'always' } },
    Card: { defaultProps: { radius: 'lg', padding: 'lg', withBorder: true } },
    Paper: { defaultProps: { radius: 'lg' } },
    Badge: {
      defaultProps: { radius: 'sm', size: 'lg' },
      styles: { root: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 } },
    },
    Alert: { defaultProps: { radius: 'md' }, styles: { title: { fontWeight: 700 } } },
    Menu: {
      defaultProps: { shadow: 'md', radius: 'md' },
      styles: { item: { minHeight: rem(48), fontSize: rem(16) }, label: { fontSize: rem(14) } },
    },
    Modal: {
      defaultProps: { radius: 'lg', centered: true },
      styles: {
        title: { fontFamily: '"Fraunces", Georgia, serif', fontWeight: 600, fontSize: rem(21), lineHeight: 1.25 },
      },
    },
  },
});

/**
 * Maps Mantine's own semantic variables onto the Carpool tokens, per scheme, so components we
 * don't style by hand (help text, placeholders, menus, alerts, inputs) pass AA as well.
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-body': 'var(--rides-surface)',
    '--mantine-color-text': 'var(--rides-ink)',
    '--mantine-color-dimmed': 'var(--rides-muted)',
    '--mantine-color-placeholder': 'var(--rides-placeholder)',
    '--mantine-color-error': 'var(--rides-bad)',
    '--mantine-color-anchor': 'var(--rides-primary)',
    '--mantine-color-default': 'var(--rides-surface)',
    '--mantine-color-default-hover': 'var(--rides-surface-2)',
    '--mantine-color-default-color': 'var(--rides-ink)',
    '--mantine-color-default-border': 'var(--rides-input-line)',
  },
  dark: {
    '--mantine-color-body': 'var(--rides-surface)',
    '--mantine-color-text': 'var(--rides-ink)',
    '--mantine-color-dimmed': 'var(--rides-muted)',
    '--mantine-color-placeholder': 'var(--rides-placeholder)',
    '--mantine-color-error': 'var(--rides-bad)',
    '--mantine-color-anchor': 'var(--rides-primary)',
    '--mantine-color-default': 'var(--rides-surface-2)',
    '--mantine-color-default-hover': 'var(--rides-raise)',
    '--mantine-color-default-color': 'var(--rides-ink)',
    '--mantine-color-default-border': 'var(--rides-input-line)',
  },
});
